// Cloudinary delivery-transform helper.
//
// Stored image URLs are raw Cloudinary originals (full resolution, no
// transforms). Rendering those originals into small card/avatar slots means the
// browser downloads + decodes images far larger than the slot — slow on mobile,
// heavy on data, and on the iOS WKWebView image decoder it triggers a storm of
// `'WEBP' ... initImage failed err=-50` log noise when a salon list decodes
// dozens of full-size images at once. Asking Cloudinary for a right-sized image
// (f_auto picks the best format per browser, q_auto the quality) fixes all
// three: smaller download, lighter decode, quiet logs.
//
// `cld(url, width)` inserts the transform segment right after `/image/upload/`.
// It is a pure string transform with conservative pass-through: anything that
// isn't a plain Cloudinary delivery URL is returned unchanged —
//   - null / empty / non-string
//   - non-Cloudinary hosts (e.g. Google `googleusercontent.com` avatars from
//     Google sign-in, gravatars, data: URIs)
//   - URLs that already carry a leading transform segment (so we never
//     double-transform an already-sized URL).

const UPLOAD = '/image/upload/';

export function cld(url, width, { crop = 'limit', height, gravity } = {}) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('res.cloudinary.com')) return url;
  const at = url.indexOf(UPLOAD);
  if (at === -1) return url;

  const rest = url.slice(at + UPLOAD.length);
  // Leave already-transformed URLs alone: if the first path segment already
  // looks like a Cloudinary transform (format/quality/width/crop), don't stack
  // another one on top.
  const firstSeg = rest.split('/')[0];
  // Each transform token must be bounded by a comma or the segment edge — `\d+`
  // for the full number and a trailing `(,|$)` — so a filename that merely
  // *starts* like a transform (e.g. `w_800_cover.jpg`, `c_limit_avatar.png`)
  // when there's no version/transform segment isn't mistaken for an
  // already-sized URL and skipped.
  if (/(^|,)(f_auto|q_auto|w_\d+|h_\d+|c_(fill|limit|scale|fit|thumb))(,|$)/.test(firstSeg)) {
    return url;
  }

  const transform = [
    'f_auto', // best format per browser (AVIF/WebP/JPEG)
    'q_auto', // automatic quality
    crop && `c_${crop}`,
    gravity && `g_${gravity}`,
    width && `w_${width}`,
    height && `h_${height}`,
  ]
    .filter(Boolean)
    .join(',');

  return `${url.slice(0, at + UPLOAD.length)}${transform}/${rest}`;
}

// Square, face-centred avatar at a retina-friendly size. Used for team /
// specialist / user avatars, which render in small round slots.
export function cldAvatar(url, size = 200) {
  return cld(url, size, { crop: 'fill', gravity: 'face', height: size });
}
